/**
 * MarketGlobe3D — Neon vector-line globe (v3 rewrite).
 *
 * Renders a translucent dark sphere with REAL country outlines drawn as
 * neon line segments from world-atlas TopoJSON (no heightmap edge tricks).
 * Each country is colored by continent (N. America = green, S. America =
 * orange, Europe/Africa/Asia = magenta-purple, Oceania = green).
 *
 * Built imperatively with vanilla three.js to bypass the visual-edits babel
 * plugin which corrupts R3F's JSX reconciler.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import * as THREE from "three";
import { feature } from "topojson-client";
import { Loader2, X, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { addWatchlist } from "@/lib/api";
import SymbolChart from "@/components/SymbolChart";
import { haptic } from "@/lib/haptic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 60 * 1000;
const WORLD_TOPO_URL = "https://unpkg.com/world-atlas@2/countries-110m.json";

const R = 1.0;

// Exchange coordinates + nearby on-globe label coordinates
const INDEXES = [
  { symbol: "^GSPC",  label: "SPX",    city: "NEW YORK",  lat: 40.71, lng: -74.00,  labelLat: 38,   labelLng: -88,  session: "NY"  },
  { symbol: "^IXIC",  label: "NASDAQ", city: "NEW YORK",  lat: 40.71, lng: -74.00,  labelLat: 32,   labelLng: -86,  session: "NY"  },
  { symbol: "^FTSE",  label: "FTSE",   city: "LONDON",    lat: 51.50, lng:  -0.13,  labelLat: 55,   labelLng:  -5,  session: "LDN" },
  { symbol: "^GDAXI", label: "DAX",    city: "FRANKFURT", lat: 50.11, lng:   8.68,  labelLat: 47,   labelLng:  18,  session: "LDN" },
  { symbol: "^N225",  label: "N225",   city: "TOKYO",     lat: 35.68, lng: 139.69,  labelLat: 40,   labelLng: 132,  session: "TYO" },
  { symbol: "^HSI",   label: "HSI",    city: "HONG KONG", lat: 22.30, lng: 114.17,  labelLat: 16,   labelLng: 110,  session: "TYO" },
];

const fmtPrice = (v) => {
  if (v == null) return "—";
  if (v >= 10000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const shortestDelta = (from, to) => {
  return ((to - from + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
};

// Lat/lng (degrees) → unit vector on the sphere.
const latLngToVec3 = (lat, lng, radius = R) => {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
     radius * Math.cos(phi),
     radius * Math.sin(phi) * Math.sin(theta),
  );
};

// Approx polygon centroid (in lat/lng space) — used to pick a continent color.
const centroidOf = (coords) => {
  let sx = 0, sy = 0, n = 0;
  for (const ring of coords) {
    for (const [lng, lat] of ring) { sx += lng; sy += lat; n++; }
  }
  return n ? [sx / n, sy / n] : [0, 0];
};

// Continent ID enum — passed as a per-vertex attribute so the line shader
// can dim continents whose home session is currently closed.
const CONT_NA = 0;
const CONT_SA = 1;
const CONT_EU = 2;
const CONT_AF = 3;
const CONT_AS = 4;
const CONT_OC = 5;
const CONT_AQ = 6;  // Antarctica → always dim

// Continent → session-code mapping (controls dimming)
const CONT_SESSION = ["NY", "NY", "LDN", "LDN", "TYO", "SYD", null];

// Pick a neon color + continent id for a country based on its centroid.
const continentForCentroid = (lng, lat) => {
  if (lat > 8 && lat < 80 && lng > -170 && lng < -50) return { color: [0.10, 1.00, 0.45], cid: CONT_NA };
  if (lat > 60 && lng > -55 && lng < -15)              return { color: [0.10, 1.00, 0.45], cid: CONT_NA };
  if (lat > -56 && lat < 15 && lng > -82 && lng < -34) return { color: [1.00, 0.55, 0.10], cid: CONT_SA };
  if (lat > -50 && lat < -10 && lng > 110 && lng < 180) return { color: [0.20, 1.00, 0.55], cid: CONT_OC };
  if (lat > -20 && lat < 0  && lng > 130 && lng < 180)  return { color: [0.20, 1.00, 0.55], cid: CONT_OC };
  if (lat > 36 && lat < 72 && lng > -12 && lng < 50)   return { color: [0.30, 1.00, 0.85], cid: CONT_EU };
  if (lat > -36 && lat < 38 && lng > -18 && lng < 52)  return { color: [0.95, 0.40, 1.00], cid: CONT_AF };
  if (lat > -10 && lat < 80 && lng > 25 && lng < 180)  return { color: [0.55, 0.50, 1.00], cid: CONT_AS };
  return { color: [0.30, 0.85, 1.00], cid: CONT_AQ };
};

// Build a single THREE.LineSegments mesh from a GeoJSON FeatureCollection.
// Each polygon ring is converted to spherical line segments; vertex colors
// pick the per-country neon hue, and a per-vertex continent-id attribute
// lets the shader dim continents whose home session is closed.
function buildCountryLines(geojson, radius = R * 1.001, opacity = 0.95) {
  const positions = [];
  const colors = [];
  const cids = [];
  const SEG_STEP = 2;

  const pushSegment = (p1, p2, color, cid) => {
    // Skip segments that cross the antimeridian (±180° wrap) — these produce
    // straight horizontal lines spanning the globe (a false "equator ring"
    // through every continent that touches the date line).  Same trick
    // protects against Antarctica's south-pole loop which has artificial
    // segments connecting -180 → +180 at lat ~-89.
    if (Math.abs(p1[0] - p2[0]) > 180) return;
    // Skip degenerate near-pole edges (Antarctica fills around the south pole)
    if (p1[1] < -85 && p2[1] < -85) return;

    const dist = Math.hypot(p1[0] - p2[0], p1[1] - p2[1]);
    const steps = Math.max(1, Math.ceil(dist / SEG_STEP));
    let prev = latLngToVec3(p1[1], p1[0], radius);
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const lng = p1[0] + (p2[0] - p1[0]) * t;
      const lat = p1[1] + (p2[1] - p1[1]) * t;
      const cur = latLngToVec3(lat, lng, radius);
      positions.push(prev.x, prev.y, prev.z, cur.x, cur.y, cur.z);
      colors.push(...color, ...color);
      cids.push(cid, cid);
      prev = cur;
    }
  };

  for (const f of geojson.features) {
    const geom = f.geometry;
    if (!geom) continue;
    const polys = geom.type === "Polygon" ? [geom.coordinates]
               : geom.type === "MultiPolygon" ? geom.coordinates
               : [];
    const firstRing = polys[0]?.[0] || [];
    const [cLng, cLat] = centroidOf([firstRing]);
    const { color, cid } = continentForCentroid(cLng, cLat);

    for (const poly of polys) {
      for (const ring of poly) {
        for (let i = 0; i < ring.length - 1; i++) {
          pushSegment(ring[i], ring[i + 1], color, cid);
        }
      }
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position",  new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute("color",     new THREE.Float32BufferAttribute(colors,    3));
  geo.setAttribute("continent", new THREE.Float32BufferAttribute(cids,      1));

  // Shader material that respects a per-continent dim factor.
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uOpacity: { value: opacity },
      uContDim: { value: [1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 0.35] },  // 7 continents
    },
    vertexShader: `
      attribute float continent;
      varying vec3 vColor;
      varying float vDim;
      uniform float uContDim[7];
      void main() {
        vColor = color;
        int idx = int(continent + 0.5);
        // GLSL ES 100 can't index uniform arrays with non-const ints, so use a chain.
        float d = uContDim[6];
        if (idx == 0) d = uContDim[0];
        else if (idx == 1) d = uContDim[1];
        else if (idx == 2) d = uContDim[2];
        else if (idx == 3) d = uContDim[3];
        else if (idx == 4) d = uContDim[4];
        else if (idx == 5) d = uContDim[5];
        vDim = d;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying float vDim;
      uniform float uOpacity;
      void main() {
        gl_FragColor = vec4(vColor * vDim, uOpacity * (0.35 + 0.65 * vDim));
      }
    `,
    vertexColors: true,
  });
  return new THREE.LineSegments(geo, mat);
}

// ---------- Imperative Three.js scene ----------
function useEarthScene({
  containerRef,
  sessionByCodeRef,
  indexBySymbolRef,
  focusedSymbolRef,
  labelRefs,
  dotRefs,
}) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let width = container.clientWidth;
    let height = container.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 0, 3.25);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.style.touchAction = "none";
    container.appendChild(renderer.domElement);

    // === Star background ===
    {
      const starGeo = new THREE.BufferGeometry();
      const count = 1500;
      const positions = new Float32Array(count * 3);
      const colors = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) {
        const r = 18 + Math.random() * 32;
        const phi = Math.acos(2 * Math.random() - 1);
        const theta = Math.random() * Math.PI * 2;
        positions[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
        positions[i * 3 + 1] = r * Math.cos(phi);
        positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
        const t = Math.random();
        if (t < 0.08) { colors[i*3]=0.3; colors[i*3+1]=0.75; colors[i*3+2]=1.0; }
        else if (t < 0.16) { colors[i*3]=0.2; colors[i*3+1]=1.0; colors[i*3+2]=0.95; }
        else { const k = 0.7 + Math.random() * 0.3; colors[i*3]=k; colors[i*3+1]=k; colors[i*3+2]=k; }
      }
      starGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      starGeo.setAttribute("color",    new THREE.BufferAttribute(colors, 3));
      scene.add(new THREE.Points(starGeo, new THREE.PointsMaterial({
        size: 0.05, transparent: true, opacity: 0.8, vertexColors: true, depthWrite: false,
      })));
    }

    // === Globe group (yaw + pitch applied here) ===
    const earthGroup = new THREE.Group();
    scene.add(earthGroup);

    // Glass-like sphere body — solid dark navy with view-angle gradient
    const bodyMat = new THREE.ShaderMaterial({
      transparent: false,
      uniforms: {
        uOcean: { value: new THREE.Color(0x020714) },
        uRim:   { value: new THREE.Color(0x2db4ff) },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vWorldPos;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uOcean;
        uniform vec3 uRim;
        varying vec3 vNormal;
        varying vec3 vWorldPos;
        void main() {
          vec3 viewDir = normalize(cameraPosition - vWorldPos);
          float ndotv = max(0.0, dot(normalize(vNormal), viewDir));
          vec3 col = uOcean * (0.7 + 0.5 * ndotv);
          // Inner fresnel sheen
          float inner = pow(1.0 - ndotv, 2.8);
          col += uRim * inner * 0.45;
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const body = new THREE.Mesh(new THREE.SphereGeometry(R, 96, 96), bodyMat);
    earthGroup.add(body);

    // (Latitude/longitude wireframe rings intentionally removed — keep the
    // sphere clean so only the neon country outlines read.)

    // Country line container — populated async after TopoJSON loads
    const linesGroup = new THREE.Group();
    earthGroup.add(linesGroup);

    // Outer atmosphere glow (back-side)
    const atmoMat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, side: THREE.BackSide,
      uniforms: {
        c1: { value: new THREE.Color(0x2db4ff) },
        c2: { value: new THREE.Color(0x9fe9ff) },
      },
      vertexShader: `
        varying vec3 vNormal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vNormal;
        uniform vec3 c1; uniform vec3 c2;
        void main() {
          float i = pow(0.62 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 3.0);
          vec3 col = mix(c1, c2, vNormal.y * 0.5 + 0.5);
          gl_FragColor = vec4(col, 1.0) * i;
        }
      `,
    });
    const atmo = new THREE.Mesh(new THREE.SphereGeometry(R, 64, 64), atmoMat);
    atmo.scale.setScalar(1.05);
    scene.add(atmo);

    // === Load country outlines from world-atlas TopoJSON ===
    // We keep refs to the line meshes so the session-refresh code can update
    // each one's per-continent dim uniform when markets open/close.
    let lineMeshes = [];
    let cancelled = false;
    fetch(WORLD_TOPO_URL)
      .then((r) => r.json())
      .then((topo) => {
        if (cancelled) return;
        const countries = feature(topo, topo.objects.countries);
        const main  = buildCountryLines(countries, R * 1.001, 0.95);
        const glow1 = buildCountryLines(countries, R * 1.004, 0.32);
        const glow2 = buildCountryLines(countries, R * 1.008, 0.14);
        lineMeshes = [main, glow1, glow2];
        lineMeshes.forEach((m) => linesGroup.add(m));
        // Apply whatever the latest session state is (may have loaded already)
        applyDimFromSessions();
      })
      .catch(() => {
        /* if the network blob fails the globe still renders without lines */
      });

    // Compute per-continent dim factor (1.0 when in trading hours, 0.32 when
    // closed) from the live session map and push it to every line mesh's
    // shader uniform.
    const applyDimFromSessions = () => {
      const sessions = sessionByCodeRef.current || {};
      const dim = [0, 0, 0, 0, 0, 0, 0.25];
      for (let i = 0; i < 6; i++) {
        const sessCode = CONT_SESSION[i];
        const open = !!sessions[sessCode]?.is_open;
        dim[i] = open ? 1.0 : 0.32;
      }
      // Asia gets a bonus boost when TYO is open (visually larger area)
      lineMeshes.forEach((m) => {
        if (m.material?.uniforms?.uContDim) {
          m.material.uniforms.uContDim.value = dim;
        }
      });
    };
    // Listen for session refresh events from the data layer
    const onSessionUpdate = () => applyDimFromSessions();
    window.addEventListener("globe-sessions-updated", onSessionUpdate);

    // Pre-compute label anchor points
    const labelPoints = INDEXES.map((idx) => ({
      idx,
      pos: latLngToVec3(idx.lat, idx.lng, R * 1.012),
      labelPos: latLngToVec3(idx.labelLat, idx.labelLng, R * 1.012),
    }));

    const Z_DEFAULT = 3.25;  // zoomed out ~20% from previous 2.7
    const Z_FOCUSED = 1.85;  // proportional zoom-in target

    // === Animation state ===
    let yaw = 0;
    let pitch = 0;
    let yawVel = 0;
    let pitchVel = 0;
    let autoRotate = true;
    let dragging = false;
    let lastDragX = 0;
    let lastDragY = 0;
    let lastFocus = null;
    let anim = null;
    let pendingClickSymbol = null;
    let pointerDownAt = null;
    let pointerDownXY = null;

    // === Pointer interaction (drag to rotate, tap to focus) ===
    const canvas = renderer.domElement;
    const _v = new THREE.Vector3();
    const labelHitTest = (e) => {
      const rect = canvas.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      let best = null;
      let bestDist = 42;
      for (const { idx, labelPos } of labelPoints) {
        _v.copy(labelPos).applyMatrix4(earthGroup.matrixWorld);
        const proj = _v.clone().project(camera);
        const sx = (proj.x * 0.5 + 0.5) * rect.width;
        const sy = (-proj.y * 0.5 + 0.5) * rect.height;
        const camToPoint = _v.clone().sub(camera.position);
        const dotVal = -camToPoint.clone().normalize().dot(_v.clone().normalize());
        if (dotVal < -0.05) continue;
        const d = Math.hypot(px - sx, py - sy);
        if (d < bestDist) { bestDist = d; best = idx.symbol; }
      }
      return best;
    };

    const onPointerDown = (e) => {
      dragging = true; autoRotate = false; anim = null;
      lastDragX = e.clientX; lastDragY = e.clientY;
      yawVel = 0; pitchVel = 0;
      pointerDownAt = performance.now();
      pointerDownXY = [e.clientX, e.clientY];
      pendingClickSymbol = labelHitTest(e);
      canvas.setPointerCapture?.(e.pointerId);
      haptic.tap();  // tactile feedback on grab
    };
    const onPointerMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastDragX;
      const dy = e.clientY - lastDragY;
      lastDragX = e.clientX; lastDragY = e.clientY;
      const k = 0.006;
      yaw += dx * k;
      pitch = Math.max(-Math.PI / 2.2, Math.min(Math.PI / 2.2, pitch - dy * k));
      yawVel = dx * k; pitchVel = -dy * k;
      // Fire a light tick every ~30deg of rotation so the user *feels* the
      // sphere clicking under their finger as it spins.
      if (Math.abs(dx) + Math.abs(dy) > 8) haptic.tick();
      if (pointerDownXY) {
        const tx = e.clientX - pointerDownXY[0];
        const ty = e.clientY - pointerDownXY[1];
        if (Math.hypot(tx, ty) > 6) pendingClickSymbol = null;
      }
    };
    const onPointerUp = (e) => {
      if (!dragging) return;
      dragging = false;
      canvas.releasePointerCapture?.(e.pointerId);
      const heldMs = performance.now() - (pointerDownAt || 0);
      const moved = pointerDownXY
        ? Math.hypot(e.clientX - pointerDownXY[0], e.clientY - pointerDownXY[1])
        : 99;
      if (pendingClickSymbol && heldMs < 400 && moved < 6) {
        focusedSymbolRef.current = pendingClickSymbol;
        haptic.success();  // double-pulse on successful label tap
        window.dispatchEvent(new CustomEvent("globe-label-tap", {
          detail: { symbol: pendingClickSymbol },
        }));
      }
      pendingClickSymbol = null;
      pointerDownAt = null; pointerDownXY = null;
    };
    canvas.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);

    // === Main loop ===
    let last = performance.now();
    let raf = 0;
    const tick = () => {
      const now = performance.now();
      const dt = (now - last) / 1000;
      last = now;

      const focused = focusedSymbolRef.current;
      if (focused !== lastFocus) {
        if (focused) {
          const idx = INDEXES.find((i) => i.symbol === focused);
          if (idx) {
            const targetYaw = -((idx.labelLng + 180) * Math.PI) / 180;
            const targetPitch = (idx.labelLat * Math.PI) / 180;
            anim = {
              t0: now / 1000, dur: 1.05,
              startYaw: yaw, startPitch: pitch,
              startZ: camera.position.z,
              dYaw: shortestDelta(yaw, targetYaw),
              dPitch: targetPitch - pitch,
              dZ: Z_FOCUSED - camera.position.z,
              resume: false,
            };
            autoRotate = false;
            yawVel = 0; pitchVel = 0;
          }
        } else {
          anim = {
            t0: now / 1000, dur: 0.8,
            startYaw: yaw, startPitch: pitch,
            startZ: camera.position.z,
            dYaw: 0, dPitch: -pitch,
            dZ: Z_DEFAULT - camera.position.z,
            resume: true,
          };
        }
        lastFocus = focused;
      }

      if (anim) {
        const t = Math.min(1, (now / 1000 - anim.t0) / anim.dur);
        const e = easeInOutCubic(t);
        yaw = anim.startYaw + anim.dYaw * e;
        pitch = anim.startPitch + anim.dPitch * e;
        if (anim.dZ != null) camera.position.z = anim.startZ + anim.dZ * e;
        if (t >= 1) { if (anim.resume) autoRotate = true; anim = null; }
      } else if (dragging) {
        // direct user control
      } else if (Math.abs(yawVel) > 0.0005 || Math.abs(pitchVel) > 0.0005) {
        yaw += yawVel;
        pitch = Math.max(-Math.PI / 2.2, Math.min(Math.PI / 2.2, pitch + pitchVel));
        yawVel *= 0.94; pitchVel *= 0.94;
      } else if (autoRotate) {
        yaw = (yaw + dt * 0.08) % (Math.PI * 2);
      } else {
        tick._idle = (tick._idle || 0) + dt;
        if (tick._idle > 4 && !focused) { autoRotate = true; tick._idle = 0; }
      }
      if (dragging || Math.abs(yawVel) > 0.0005) tick._idle = 0;

      earthGroup.rotation.y = yaw;
      earthGroup.rotation.x = pitch;

      // === Overlay updates (labels + city dots) ===
      const indexBySymbol = indexBySymbolRef.current || {};
      const sessionByCode = sessionByCodeRef.current || {};
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const focusedSym = focused;

      labelPoints.forEach(({ idx, pos, labelPos }) => {
        const wCity = pos.clone().applyMatrix4(earthGroup.matrixWorld);
        const cityProj = wCity.clone().project(camera);
        const ax = (cityProj.x * 0.5 + 0.5) * w;
        const ay = (-cityProj.y * 0.5 + 0.5) * h;
        const camToCity = wCity.clone().sub(camera.position);
        const cityDotVal = -camToCity.clone().normalize().dot(wCity.clone().normalize());

        const wLabel = labelPos.clone().applyMatrix4(earthGroup.matrixWorld);
        const labelProj = wLabel.clone().project(camera);
        const lx = (labelProj.x * 0.5 + 0.5) * w;
        const ly = (-labelProj.y * 0.5 + 0.5) * h;
        const camToLabel = wLabel.clone().sub(camera.position);
        const labelDotVal = -camToLabel.clone().normalize().dot(wLabel.clone().normalize());

        const visible = labelDotVal > -0.05 && labelProj.z < 1;
        const opacity = visible ? Math.min(1, 0.3 + (labelDotVal + 0.05) * 1.5) : 0;
        const focusScale = focusedSym === idx.symbol ? 1.20 : 1;

        const labelEl = labelRefs.current[idx.symbol];
        if (labelEl) {
          labelEl.style.transform = `translate(-50%, -50%) translate(${lx.toFixed(1)}px, ${ly.toFixed(1)}px) scale(${focusScale})`;
          labelEl.style.opacity = opacity.toFixed(2);
          labelEl.style.pointerEvents = visible ? "auto" : "none";

          const data = indexBySymbol[idx.symbol];
          const pct = data?.change_pct ?? 0;
          const priceLine = labelEl.querySelector("[data-l='price']");
          const cityLine = labelEl.querySelector("[data-l='city']");
          if (priceLine) {
            const text = `${idx.label} ${pct > 0 ? "+" : ""}${(pct ?? 0).toFixed(2)}%`;
            if (priceLine.textContent !== text) priceLine.textContent = text;
            const color = pct > 0 ? "#00ff88" : pct < 0 ? "#ff5d4a" : "#cfd0d6";
            const glow = pct > 0
              ? "0 0 6px rgba(0,255,140,0.95), 0 0 16px rgba(0,255,140,0.7), 0 0 36px rgba(0,255,140,0.45)"
              : pct < 0
              ? "0 0 6px rgba(255,90,70,0.95), 0 0 16px rgba(255,90,70,0.7), 0 0 36px rgba(255,90,70,0.45)"
              : "0 0 6px rgba(220,220,255,0.7)";
            if (priceLine.style.color !== color) priceLine.style.color = color;
            priceLine.style.textShadow = glow;
          }
          if (cityLine) {
            const sessOpen = !!sessionByCode[idx.session]?.is_open;
            const tone = sessOpen ? "rgba(50,224,255,0.95)" : "rgba(255,255,255,0.55)";
            if (cityLine.style.color !== tone) cityLine.style.color = tone;
          }
        }

        const dotEl = dotRefs.current[idx.symbol];
        if (dotEl) {
          dotEl.setAttribute("cx", ax.toFixed(1));
          dotEl.setAttribute("cy", ay.toFixed(1));
          const cityVisible = cityDotVal > -0.05 && cityProj.z < 1;
          dotEl.setAttribute("opacity", cityVisible ? "1" : "0");
          const sessOpen = !!sessionByCode[idx.session]?.is_open;
          const fill = sessOpen ? "#00ff88" : "#32e0ff";
          if (dotEl.getAttribute("fill") !== fill) dotEl.setAttribute("fill", fill);
        }
      });

      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    // === Resize handling ===
    const onResizeLocal = () => {
      width = container.clientWidth;
      height = container.clientHeight;
      if (width > 0 && height > 0) {
        renderer.setSize(width, height);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      }
    };
    const ro = new ResizeObserver(onResizeLocal);
    ro.observe(container);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      window.removeEventListener("globe-sessions-updated", onSessionUpdate);
      renderer.dispose();
      body.geometry.dispose();
      bodyMat.dispose();
      atmo.geometry.dispose();
      atmoMat.dispose();
      linesGroup.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
      });
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ---------- Index detail modal ----------
const IndexDetailModal = ({ symbol, indicesRef, onClose }) => {
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  useEffect(() => { setAdded(false); }, [symbol]);

  if (!symbol) return null;
  const indices = indicesRef?.current || [];
  const focus = indices.find((i) => i.symbol === symbol);
  if (!focus) return null;

  const idxMeta = INDEXES.find((i) => i.symbol === symbol);
  const label = idxMeta?.label || focus.label || focus.symbol;
  const pct = focus.change_pct ?? 0;
  const focusColor = pct > 0 ? "#00E58E" : pct < 0 ? "#FF5A4A" : "#A1A1AA";

  const handleAdd = async () => {
    if (!user) { toast.error("Sign in to save indices to your watchlist"); return; }
    if (added || adding) return;
    setAdding(true);
    try {
      await addWatchlist(symbol, label);
      setAdded(true);
      toast.success(`${label} added to watchlist`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not add to watchlist";
      if (typeof msg === "string" && msg.toLowerCase().includes("already")) {
        setAdded(true);
        toast.info(`${label} is already in your watchlist`);
      } else if (err?.response?.status === 402) {
        toast.error("Free tier capped at 5 — upgrade to Pro for unlimited");
      } else {
        toast.error(typeof msg === "string" ? msg : "Could not add to watchlist");
      }
    } finally { setAdding(false); }
  };

  const maxLen = Math.max(...indices.map((i) => (i.sparkline || []).length || 0));
  const compSeries = [];
  for (let i = 0; i < maxLen; i++) {
    const row = { i };
    indices.forEach((ix) => {
      const sp = ix.sparkline || [];
      const base = sp[0];
      const cur = sp[i];
      if (base != null && cur != null) row[ix.symbol] = ((cur - base) / base) * 100;
    });
    compSeries.push(row);
  }
  const COMP_COLORS = {
    "^GSPC":  "#FFD60A", "^IXIC":  "#A98CFF", "^FTSE":  "#34C759",
    "^GDAXI": "#00E5E5", "^N225":  "#FF6B9C", "^HSI":   "#FF9F0A",
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         onClick={onClose} data-testid="globe-index-modal">
      <div className="relative w-full max-w-3xl bg-[#070710] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-white/8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">{focus.region || idxMeta?.city || "—"}</span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/30">·</span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 num">{focus.symbol}</span>
            </div>
            <div className="flex items-baseline gap-3">
              <h3 className="text-2xl font-semibold tracking-tight">{label}</h3>
              <span className="num text-base text-white/85">{fmtPrice(focus.last)}</span>
              <span className="num text-sm font-medium" style={{ color: focusColor }}>
                {pct > 0 ? "+" : ""}{(pct ?? 0).toFixed(2)}%
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button type="button" onClick={handleAdd} disabled={adding || added}
                    data-testid="globe-index-add-watchlist"
                    className={
                      "inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md border transition disabled:cursor-not-allowed " +
                      (added ? "border-[#00C805]/40 bg-[#00C805]/10 text-[#00C805]"
                             : "border-white/15 hover:border-white/35 text-white/75 hover:text-white")
                    }>
              {added ? (<><Check className="w-3 h-3" /> Saved</>)
                : adding ? (<><Loader2 className="w-3 h-3 animate-spin" /> Adding…</>)
                : (<><Plus className="w-3 h-3" /> Watchlist</>)}
            </button>
            <button type="button" onClick={onClose}
                    className="p-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/5 transition"
                    aria-label="Close" data-testid="globe-index-modal-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-5 pt-4">
          <SymbolChart symbol={symbol} period="3mo" height={240} />
        </div>

        <div className="px-5 pt-4 pb-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Compare · % change (normalized)</div>
          </div>
          <div className="h-[150px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={compSeries} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                <XAxis dataKey="i" hide />
                <YAxis tickFormatter={(v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`}
                       tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }}
                       width={56} axisLine={false} tickLine={false} />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.18)" strokeDasharray="3 3" />
                <Tooltip
                  contentStyle={{ background: "#0a0a12", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, fontSize: 11 }}
                  formatter={(v, n) => {
                    const meta = INDEXES.find((i) => i.symbol === n);
                    return [`${v >= 0 ? "+" : ""}${v.toFixed(2)}%`, meta?.label || n];
                  }}
                  labelFormatter={(i) => `Pt ${i}`} />
                {INDEXES.map((idx) => (
                  <Line key={idx.symbol} type="monotone" dataKey={idx.symbol}
                        stroke={COMP_COLORS[idx.symbol]}
                        strokeWidth={idx.symbol === symbol ? 2.2 : 1.1}
                        dot={false} isAnimationActive={false}
                        opacity={idx.symbol === symbol ? 1 : 0.55} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5">
            {INDEXES.map((idx) => {
              const ix = indices.find((i) => i.symbol === idx.symbol);
              const ixPct = ix?.change_pct ?? 0;
              const isFocus = idx.symbol === symbol;
              return (
                <div key={idx.symbol}
                     className={`inline-flex items-center gap-1.5 text-[10px] ${isFocus ? "text-white" : "text-white/65"}`}>
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: COMP_COLORS[idx.symbol] }} aria-hidden />
                  <span className="uppercase tracking-[0.12em] font-medium">{idx.label}</span>
                  <span className="num text-white/55">{fmtPrice(ix?.last)}</span>
                  <span className="num"
                        style={{ color: ixPct > 0 ? "#00E58E" : ixPct < 0 ? "#FF5A4A" : "#A1A1AA" }}>
                    {ixPct > 0 ? "+" : ""}{(ixPct ?? 0).toFixed(2)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Top-level component ----------
const MarketGlobe3D = () => {
  const sessionByCodeRef = useRef({});
  const indexBySymbolRef = useRef({});
  const indicesRef = useRef([]);
  const utcStampRef = useRef(null);
  const focusedSymbolRef = useRef(null);
  const containerRef = useRef(null);
  const labelRefs = useRef({});
  const dotRefs = useRef({});

  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [pendingSymbol, setPendingSymbol] = useState(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  useEarthScene({
    containerRef,
    sessionByCodeRef,
    indexBySymbolRef,
    focusedSymbolRef,
    labelRefs,
    dotRefs,
  });

  useEffect(() => {
    const onTap = (e) => {
      const sym = e?.detail?.symbol;
      if (!sym) return;
      setPendingSymbol(sym);
      window.setTimeout(() => setSelectedSymbol(sym), 1100);
    };
    window.addEventListener("globe-label-tap", onTap);
    return () => window.removeEventListener("globe-label-tap", onTap);
  }, []);

  const handleLabelClick = (sym) => {
    focusedSymbolRef.current = sym;
    setPendingSymbol(sym);
    window.setTimeout(() => setSelectedSymbol(sym), 1100);
  };

  const handleClose = () => {
    setSelectedSymbol(null);
    setPendingSymbol(null);
    focusedSymbolRef.current = null;
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/market/global-overview`);
        if (cancelled) return;
        const data = r.data || {};
        const nextSessions = {};
        (data.sessions || []).forEach((s) => { nextSessions[s.code] = s; });
        sessionByCodeRef.current = nextSessions;
        const nextIndices = {};
        (data.indices || []).forEach((i) => { nextIndices[i.symbol] = i; });
        indexBySymbolRef.current = nextIndices;
        indicesRef.current = data.indices || [];
        // Notify the 3D scene that session state changed so it can re-apply
        // the per-continent dim factors.
        window.dispatchEvent(new CustomEvent("globe-sessions-updated"));
        if (utcStampRef.current) {
          utcStampRef.current.textContent =
            `GLOBAL MARKETS · ${data.utc_time || "—"} · DRAG TO ROTATE · TAP AN INDEX FOR DETAILS`;
        }
        if (!initialLoaded) setInitialLoaded(true);
      } catch { /* degrade silently */ }
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto w-full max-w-[560px] mb-3" data-testid="market-globe"
         aria-label="Global markets neon vector globe">
      <div
        ref={containerRef}
        className="relative w-full select-none"
        style={{ aspectRatio: "1 / 1", maxHeight: 540, cursor: "grab" }}
        data-testid="market-globe-svg"
        onMouseDown={(e) => { e.currentTarget.style.cursor = "grabbing"; }}
        onMouseUp={(e) => { e.currentTarget.style.cursor = "grab"; }}
      >
        {/* SVG overlay: city dots (positioned above the canvas) */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          aria-hidden
        >
          <defs>
            <filter id="dotGlow" x="-200%" y="-200%" width="500%" height="500%">
              <feGaussianBlur stdDeviation="2.2" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {INDEXES.map((idx) => (
            <circle
              key={`dot-${idx.symbol}`}
              ref={(el) => { dotRefs.current[idx.symbol] = el; }}
              cx="0" cy="0" r="3"
              fill="#32e0ff"
              opacity="0"
              filter="url(#dotGlow)"
            />
          ))}
        </svg>

        {/* Label overlay (HTML, positioned imperatively each frame) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ pointerEvents: "none" }}
        >
          {INDEXES.map((idx) => (
            <div
              key={idx.symbol}
              ref={(el) => { labelRefs.current[idx.symbol] = el; }}
              data-testid={`globe-blob-${idx.symbol}`}
              onClick={() => handleLabelClick(idx.symbol)}
              className="absolute left-0 top-0"
              style={{
                fontFamily: "JetBrains Mono, monospace",
                textAlign: "center",
                whiteSpace: "nowrap",
                cursor: "pointer",
                transition: "opacity 220ms ease",
                pointerEvents: "auto",
                willChange: "transform, opacity",
                userSelect: "none",
                padding: "5px 11px 6px",
                borderRadius: 10,
                background: "rgba(2, 5, 14, 0.78)",
                border: "1px solid rgba(180, 220, 255, 0.18)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                boxShadow: "0 0 18px rgba(0, 0, 0, 0.55), inset 0 0 8px rgba(50, 224, 255, 0.06)",
              }}
            >
              <div
                data-l="price"
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  color: "#ffffff",
                  textShadow: "0 0 6px rgba(255,255,255,0.35)",
                  lineHeight: 1.05,
                }}
              >
                {idx.label}
              </div>
              <div
                data-l="city"
                style={{
                  fontSize: 9.5,
                  letterSpacing: "0.28em",
                  color: "rgba(180,220,255,0.85)",
                  marginTop: 3,
                  textShadow: "0 0 4px rgba(0,0,0,0.9)",
                }}
              >
                {idx.city}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-1 text-center">
        {!initialLoaded ? (
          <span className="inline-flex items-center gap-1.5 text-[10px] text-white/45">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading global markets…
          </span>
        ) : (
          <span ref={utcStampRef}
                className="num text-[9px] tracking-[0.28em] text-white/40 uppercase"
                data-testid="globe-utc" />
        )}
        {pendingSymbol && !selectedSymbol && (
          <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-[#32e0ff]"
               style={{ textShadow: "0 0 8px rgba(50,224,255,0.7)" }}>
            Zooming to {INDEXES.find((i) => i.symbol === pendingSymbol)?.city}…
          </div>
        )}
      </div>

      <IndexDetailModal symbol={selectedSymbol} indicesRef={indicesRef} onClose={handleClose} />
    </div>
  );
};

export default MarketGlobe3D;
